/**
 * Utility functions for temporary login with automatic cleanup
 * Stores previous auth token with expiration to prevent token staying in localStorage forever
 */

const TEMP_LOGIN_KEY = 'temp_login_data'
const EXPIRY_HOURS = 24 // Token expires after 24 hours

interface TempLoginData {
  previousToken: string
  previousUser: Record<string, unknown> // Store complete user record
  tempUserId: string
  expiresAt: number // Unix timestamp
}

/**
 * Save the current auth token and user record before temporary login
 * @param currentToken Current authentication token
 * @param currentUser Current user record
 * @param tempUserId ID of the user we're logging in as temporarily
 */
export function savePreviousAuth(
  currentToken: string,
  currentUser: Record<string, unknown>,
  tempUserId: string
): void {
  const expiresAt = Date.now() + EXPIRY_HOURS * 60 * 60 * 1000

  const data: TempLoginData = {
    previousToken: currentToken,
    previousUser: currentUser,
    tempUserId,
    expiresAt,
  }

  localStorage.setItem(TEMP_LOGIN_KEY, JSON.stringify(data))

  // Dispatch custom event to notify components in the same tab
  window.dispatchEvent(new CustomEvent('temp-login-changed'))
}

/**
 * Get the previous auth token if still valid
 * @returns TempLoginData or null if expired/not found
 */
export function getPreviousAuth(): TempLoginData | null {
  const stored = localStorage.getItem(TEMP_LOGIN_KEY)
  if (!stored) return null

  try {
    const data: TempLoginData = JSON.parse(stored)

    // Check if expired
    if (Date.now() > data.expiresAt) {
      clearPreviousAuth()
      return null
    }

    return data
  } catch {
    // Invalid JSON, clear it
    clearPreviousAuth()
    return null
  }
}

/**
 * Clear the stored previous auth token
 */
export function clearPreviousAuth(): void {
  localStorage.removeItem(TEMP_LOGIN_KEY)

  // Dispatch custom event to notify components
  window.dispatchEvent(new CustomEvent('temp-login-changed'))
}

/**
 * Check if currently in temporary login mode
 */
export function isTemporaryLogin(): boolean {
  return getPreviousAuth() !== null
}

/**
 * Get the temporary user ID if in temp login mode
 */
export function getTempUserId(): string | null {
  const data = getPreviousAuth()
  return data?.tempUserId ?? null
}

/**
 * Clean up expired temp login tokens on app start
 * Call this once during app initialization
 */
export function cleanupExpiredTempLogin(): void {
  const data = getPreviousAuth()
  if (data && Date.now() > data.expiresAt) {
    clearPreviousAuth()
  }
}
